import React, { useState, useEffect } from 'react';
import './OABDetailsModal.css';

const OABDetailsModal = ({ isOpen, onClose, oabGroup, onSave }) => {
  const [lawyerStatus, setLawyerStatus] = useState('');
  const [comment, setComment] = useState('');
  const [precatorioStatuses, setPrecatorioStatuses] = useState({});
  const [loading, setSaving] = useState(false);

  useEffect(() => {
    if (oabGroup) {
      setLawyerStatus(oabGroup.status || 'não contatado');
      setComment(oabGroup.comment || '');
      
      // Initialize precatorio statuses
      const initialStatuses = {};
      oabGroup.precatorios?.forEach(prec => {
        initialStatuses[prec.numero_precatorio] = prec.status || 'disponível';
      });
      setPrecatorioStatuses(initialStatuses);
    }
  }, [oabGroup]);

  const handlePrecatorioStatusChange = (numeroPrecatorio, newStatus) => {
    setPrecatorioStatuses(prev => ({
      ...prev,
      [numeroPrecatorio]: newStatus
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        oab: oabGroup.oab,  // For MT, we use oab field instead of nome
        nome: oabGroup.nome, // But also include nome for compatibility
        lawyerStatus,
        comment,
        precatorioStatuses
      });
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !oabGroup) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const lawyerStatusOptions = [
    'não contatado',
    'atende',
    'não atende',
    'não quer vender',
    'já vendeu tudo',
    'vendendo'
  ];

  const precatorioStatusOptions = [
    'disponível',
    'em negociação',
    'vendido',
    'indisponível'
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Detalhes do Advogado</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Lawyer Information */}
          <div className="lawyer-section">
            <h3>{oabGroup.nome || 'Advogado(a)'}</h3>
            
            <div className="oab-list">
              <strong>OABs:</strong>
              {oabGroup.oabs && oabGroup.oabs.length > 0 ? (
                oabGroup.oabs.map((oab, index) => (
                  <span key={index} className="oab-badge">
                    {oab.uf} {oab.numero}
                  </span>
                ))
              ) : (
                <span className="oab-badge">
                  {oabGroup.uf} {oabGroup.numero || oabGroup.oab}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lawyer-status">Status do Advogado:</label>
              <select
                id="lawyer-status"
                value={lawyerStatus}
                onChange={(e) => setLawyerStatus(e.target.value)}
                className="status-select"
              >
                {lawyerStatusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="comment">Comentário:</label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="comment-textarea"
                rows="3"
                placeholder="Adicione observações sobre este advogado..."
              />
            </div>

            {oabGroup.statusUpdatedBy && (
              <div className="form-group">
                <label>Última modificação:</label>
                <div className="last-update-info">
                  Por {oabGroup.statusUpdatedBy.userName} em {' '}
                  {oabGroup.statusUpdatedAt 
                    ? new Date(oabGroup.statusUpdatedAt).toLocaleString('pt-BR')
                    : '-'
                  }
                </div>
              </div>
            )}
          </div>

          {/* Precatorios Table */}
          <div className="precatorios-section">
            <h3>Precatórios ({oabGroup.precatorios?.length || 0})</h3>
            <div className="precatorios-table-container">
              <table className="precatorios-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Valor</th>
                    <th>Orçamento</th>
                    <th>Ano Pagamento</th>
                    <th>Natureza</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {oabGroup.precatorios?.map((prec, index) => (
                    <tr key={index}>
                      <td>{prec.numero_precatorio}</td>
                      <td>{formatCurrency(prec.valor_atualizado)}</td>
                      <td>{prec.orcamento || '-'}</td>
                      <td>{prec.ano_pagamento_previsto || '-'}</td>
                      <td>{prec.natureza || '-'}</td>
                      <td>
                        <select
                          value={precatorioStatuses[prec.numero_precatorio] || 'disponível'}
                          onChange={(e) => handlePrecatorioStatusChange(prec.numero_precatorio, e.target.value)}
                          className="precatorio-status-select"
                        >
                          {precatorioStatusOptions.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          <div className="summary-section">
            <div className="summary-item">
              <strong>Total de OABs:</strong> {oabGroup.totalOabs || 1}
            </div>
            <div className="summary-item">
              <strong>Total de Precatórios:</strong> {oabGroup.totalCount || 0}
            </div>
            <div className="summary-item">
              <strong>Valor Total:</strong> {formatCurrency(oabGroup.totalValue)}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn-cancel" 
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OABDetailsModal;